import React, { useEffect, useRef, useState } from "react";
import { Button, HStack } from "@chakra-ui/react";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import { useMapObjects } from "../../../context/MapObjectsContext";
import { matchKeyInText, showToast } from "../../../utilies/Utility";
import { toggleLaneMarkerStyleTool } from "../../../utilies/laneMarkerTools/Style";
import { LuPaintBucket } from "react-icons/lu";
import Type from "../../../utilies/Types";

function LaneMarkerStyleTool() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);

  const laneMarkerStyleMode = useAppStore((s) => s.laneMarkerStyleMode);
  const setLaneMarkerStyleMode = useAppStore((s) => s.setLaneMarkerStyleMode);

  const userData = useAppStore((s) => s.userData);

  const setCanHightlightEntity = useAppStore((s) => s.setCanHightlightEntity);
  const setCanOpenInfobox = useAppStore((s) => s.setCanOpenInfobox);

  const { mapObjects, setMapObjects } = useMapObjects();

  const [isShow, setIsShow] = useState(false);
  const dsRef = useRef({ lane: null, key: null });

  const prevModeRef = useRef(null); // guard first run

  useEffect(() => {
    let lane = null;
    let key = null;

    for (const k in mapObjects) {
      const f = mapObjects[k]?.file_name || "";
      if (matchKeyInText(f, "lanemarker")) {
        lane = mapObjects[k].dataSource;
        key = k;
        break;
      }
    }

    dsRef.current = { lane, key };
    setIsShow(Boolean(lane));

    if (!lane && laneMarkerStyleMode) {
      setLaneMarkerStyleMode(false);
    }
  }, [mapObjects, laneMarkerStyleMode, setLaneMarkerStyleMode]);

  useEffect(() => {
    setCanHightlightEntity(!laneMarkerStyleMode);
    setCanOpenInfobox(!laneMarkerStyleMode);
  }, [laneMarkerStyleMode, setCanHightlightEntity, setCanOpenInfobox]);

  // Toast on toggle ON/OFF (only when it actually changes)
  useEffect(() => {
    const prev = prevModeRef.current;
    prevModeRef.current = laneMarkerStyleMode;

    if (prev === null) return; // no toast on first mount

    if (laneMarkerStyleMode) {
      showToast(
        "LaneMarker Style Editor enabled.\nSelect markers, then press:\nW = color white\nY = color yellow\nD = subtype dashed\nS = subtype solid\nEsc = clear selection / exit",
        Type.NOTIFICATION.SUCCESS,
        {
          duration: 7500,
          closable: true,
        },
      );
    } else {
      showToast("LaneMarker Style Editor disabled.", Type.NOTIFICATION.INFO, {
        duration: 2000,
        closable: true,
      });
    }
  }, [laneMarkerStyleMode]);

  const handleToggle = () => {
    const n = !laneMarkerStyleMode;
    setLaneMarkerStyleMode(n);

    const { lane, key } = dsRef.current || {};
    if (!lane) {
      setLaneMarkerStyleMode(false);
      return;
    }

    toggleLaneMarkerStyleTool(
      viewerRef.current,
      function () {
        try {
          if (!key) return;
          setMapObjects((prev) => {
            const tmp = [...prev];
            if (tmp[key]) tmp[key].is_edited = true;
            return tmp;
          });
        } catch {}
      },
      !n,
      lane,
      () => setLaneMarkerStyleMode(false),
      originalCameraControls,
      userData,
    );
  };

  useEffect(() => {
    if (laneMarkerStyleMode) return;

    const { lane } = dsRef.current || {};
    try {
      toggleLaneMarkerStyleTool(viewerRef.current, () => {}, true, lane, null, originalCameraControls, userData);
    } catch {}
  }, [laneMarkerStyleMode, viewerRef]);

  useEffect(() => {
    return () => {
      const { lane } = dsRef.current || {};
      try {
        toggleLaneMarkerStyleTool(viewerRef.current, () => {}, true, lane, null, originalCameraControls, userData);
      } catch {}
      try {
        setCanHightlightEntity(true);
        setCanOpenInfobox(true);
      } catch {}
    };
  }, [viewerRef, setCanHightlightEntity, setCanOpenInfobox]);

  if (!isShow) return null;

  return (
    <HStack>
      <Button
        disabled={editMode}
        variant="solid"
        p={0}
        opacity={1}
        bg={laneMarkerStyleMode ? "green" : "white"}
        color={laneMarkerStyleMode ? "white" : "MenuText"}
        borderWidth={laneMarkerStyleMode ? 2 : 1}
        borderColor={laneMarkerStyleMode ? "green" : "gray.200"}
        onClick={handleToggle}
        aria-pressed={laneMarkerStyleMode}
      >
        <Tooltip content={laneMarkerStyleMode ? "LANE MARKER Style Editor: ON" : "LANE MARKER Style Editor"}>
          <LuPaintBucket />
        </Tooltip>
      </Button>
    </HStack>
  );
}

export default LaneMarkerStyleTool;
